import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useMap } from "providers/navigation/MapProvider";

import ButtonsAndViewsGroup from "components/layout/ButtonsAndViewsGroup";
import DeleteButton from "components/layout/DeleteButton";
import EditLandmarkPanel from "components/navigation/map/edit/EditLandmarkPanel";
import EditLandmarksSidebar from "components/navigation/map/edit/EditLandmarksSidebar";
import EditMapIndicator from "components/navigation/map/edit/EditMapIndicator";
import FunctionalButton from "components/layout/FunctionalButton";
import MapView from "components/navigation/map/MapView";
import Portal from "components/layout/Portal";

import { ZoomStep } from "lib/consts/navigation/map";

import { ReactComponent as ArrowIcon } from "assets/svgs/Arrow.svg";
import { ReactComponent as BoundaryIcon } from "assets/svgs/navigation/map/Boundary.svg";
import { ReactComponent as BrightIcon } from "assets/svgs/navigation/map/StrongLight.svg";
import { ReactComponent as DangerIcon } from "assets/svgs/navigation/map/Danger.svg";
import { ReactComponent as LandmarkIcon } from "assets/svgs/navigation/map/Landmark.svg";
import { ReactComponent as LayersIcon } from "assets/svgs/navigation/map/Layers.svg";
import { ReactComponent as MapIcon } from "assets/svgs/navbar/Map.svg";
import { ReactComponent as MinusIcon } from "assets/svgs/Minus.svg";
import { ReactComponent as PlusIcon } from "assets/svgs/Plus.svg";
import { ReactComponent as ResetIcon } from "assets/svgs/Reset.svg";
import { ReactComponent as RoadIcon } from "assets/svgs/navigation/map/Road.svg";
import { ReactComponent as SlopeIcon } from "assets/svgs/navigation/map/Slope.svg";
import { ReactComponent as SlowIcon } from "assets/svgs/navigation/map/SlowDown.svg";
import { ReactComponent as TagIcon } from "assets/svgs/navigation/map/Tag.svg";
import { ReactComponent as UnknownIcon } from "assets/svgs/navigation/map/Unknown.svg";
import { ReactComponent as WallIcon } from "assets/svgs/navigation/map/Wall.svg";

const EditMapTopBar = ({
    mapName,
    isEditLandmarksMode,
    onSetIsEditLandmarksMode = () => {},
    onBack = () => {},
}) => {
    return (
        <div className="flex w-full min-w-100  items-center justify-between border-b border-b-black70 py-2">
            <div className="flex  items-center justify-start space-x-4">
                <FunctionalButton icon={<ArrowIcon className="rotate-180" />} onClick={onBack} />
                <p className="text-h3">{mapName}</p>
            </div>
            <div className="flex  items-center justify-end space-x-3">
                <DeleteButton onClick={() => console.log("Delete map")} />
                <FunctionalButton className="button" title="Extend" icon={<PlusIcon />} />
                {false && (
                    <FunctionalButton
                        className="button w-fit"
                        title={isEditLandmarksMode ? "Edit Map" : "Edit Landmarks"}
                        icon={isEditLandmarksMode ? <MapIcon /> : <LandmarkIcon />}
                        onClick={onSetIsEditLandmarksMode}
                    />
                )}
            </div>
        </div>
    );
};

const EditMapPage = () => {
    const { selectedMap, setSelectedMap } = useMap();

    const [isEditLandmarksMode, setIsEditLandmarkMode] = useState(true);
    const [showEditLandmarksPanel, setShowEditLandmarksPanel] = useState(true);
    const [selectedLandmark, setSelectedLandmark] = useState(null);
    const navigationMapRef = useRef();
    const navigate = useNavigate();

    const isEditMapEmpty = !selectedMap || !Object.keys(selectedMap).length;

    useEffect(() => {
        if (isEditMapEmpty) navigate("..", { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Debug
    // useEffect(() => console.log(selectedMap), []);

    if (isEditMapEmpty) return;
    return (
        <div className="page-with-top-bar flex flex-col items-center justify-start space-y-4 px-6 py-5">
            <EditMapTopBar
                mapName={selectedMap.map}
                isEditLandmarksMode={isEditLandmarksMode}
                onSetIsEditLandmarksMode={() => setIsEditLandmarkMode(!isEditLandmarksMode)}
                onBack={() => {
                    setSelectedMap(null);
                    navigate("..", { replace: true });
                }}
            />
            <div id="edit-map-main-content" className="relative flex h-full w-full ">
                <EditLandmarksSidebar
                    visible={showEditLandmarksPanel}
                    landmarks={selectedMap.landmarks}
                    selectedLandmark={selectedLandmark}
                    setSelectedLandmark={setSelectedLandmark}
                    toggleVisible={() => setShowEditLandmarksPanel(!showEditLandmarksPanel)}
                />
                <div
                    className={`relative h-full w-full ${
                        isEditLandmarksMode &&
                        showEditLandmarksPanel &&
                        "max-w-[calc(100%-16.75rem)]"
                    }`}
                >
                    <ButtonsAndViewsGroup
                        className="left-2 top-2"
                        buttonsGroup={[
                            [
                                <FunctionalButton
                                    className="h-10 w-10 rounded-r5 bg-black"
                                    icon={<LandmarkIcon className="w-6" />}
                                />,
                            ],
                        ]}
                    />
                    <ButtonsAndViewsGroup
                        className="right-2 top-2"
                        visible={!isEditLandmarksMode}
                        buttonsGroupClassName="space-y-0"
                        buttonsGroup={[
                            [
                                <FunctionalButton
                                    className="flex h-15 w-15 flex-col-reverse space-x-0 space-y-1 space-y-reverse rounded-t-r5 bg-black40 transition-all hover:bg-primary2"
                                    title="Road"
                                    icon={<RoadIcon className="w-6" />}
                                />,
                                <FunctionalButton
                                    className="flex h-15 w-15 flex-col-reverse space-x-0 space-y-1 space-y-reverse bg-black40 transition-all hover:bg-primary2"
                                    title="Boundary"
                                    icon={<BoundaryIcon className="w-6" />}
                                />,
                                <FunctionalButton
                                    className="flex h-15 w-15 flex-col-reverse space-x-0 space-y-1 space-y-reverse rounded-b-r5 bg-black40 transition-all hover:bg-primary2"
                                    title="Unknown"
                                    icon={<UnknownIcon className="w-6" />}
                                />,
                            ],
                            [
                                <FunctionalButton
                                    className="flex h-15 w-15 flex-col-reverse space-x-0 space-y-1 space-y-reverse rounded-t-r5 bg-black40 transition-all hover:bg-primary2"
                                    title="Wall"
                                    icon={<WallIcon className="w-6" />}
                                />,
                                <FunctionalButton
                                    className="flex h-15 w-15 flex-col-reverse space-x-0 space-y-1 space-y-reverse bg-black40 transition-all hover:bg-yellow-400"
                                    title="Bright"
                                    icon={<BrightIcon className="w-6" />}
                                />,
                                <FunctionalButton
                                    className="flex h-15 w-15 flex-col-reverse space-x-0 space-y-1 space-y-reverse bg-black40 transition-all hover:bg-amber-500"
                                    title="Slow"
                                    icon={<SlowIcon className="w-6" />}
                                />,
                                <FunctionalButton
                                    className="flex h-15 w-15 flex-col-reverse space-x-0 space-y-1 space-y-reverse bg-black40 transition-all hover:bg-violet-700"
                                    title="Slope"
                                    icon={<SlopeIcon className="w-6" />}
                                />,
                                <FunctionalButton
                                    className="flex h-15 w-15 flex-col-reverse space-x-0 space-y-1 space-y-reverse bg-black40 transition-all hover:bg-warning"
                                    title="Danger"
                                    icon={<DangerIcon className="w-6" />}
                                />,
                                <FunctionalButton
                                    className="flex h-15 w-15 flex-col-reverse space-x-0 space-y-1 space-y-reverse rounded-b-r5 bg-black40 transition-all hover:bg-sky-600"
                                    title="Tag"
                                    icon={<TagIcon className="w-6" />}
                                />,
                            ],
                        ]}
                    />
                    <ButtonsAndViewsGroup
                        className="top-2 right-2"
                        buttonsGroupClassName="space-y-0"
                        buttonsGroup={[
                            [
                                <FunctionalButton
                                    className="h-10 w-10 rounded-r5 bg-black60 opacity-50 transition-all hover:bg-black90 hover:opacity-80"
                                    icon={<LayersIcon className="w-6" />}
                                />,
                            ],
                            [
                                <FunctionalButton
                                    className="h-10 w-10 rounded-t-r5 bg-black60 opacity-50 transition-all hover:bg-black90 hover:opacity-80"
                                    icon={<ResetIcon className="w-6" />}
                                    onClick={() => {
                                        if (!navigationMapRef.current) return;
                                        navigationMapRef.current.resetCameraPosition();
                                    }}
                                />,
                                <FunctionalButton
                                    className="h-10 w-10 bg-black60 opacity-50 transition-all hover:bg-black90 hover:opacity-80"
                                    icon={<PlusIcon className="w-6" />}
                                    onClick={() => {
                                        if (!navigationMapRef.current) return;
                                        navigationMapRef.current.zoomDelta(ZoomStep);
                                    }}
                                />,
                                <FunctionalButton
                                    className="h-10 w-10 rounded-b-r5 bg-black60 opacity-50 transition-all hover:bg-black90 hover:opacity-80"
                                    icon={<MinusIcon className="w-6" />}
                                    onClick={() => {
                                        if (!navigationMapRef.current) return;
                                        navigationMapRef.current.zoomDelta(-ZoomStep);
                                    }}
                                />,
                            ],
                        ]}
                        viewsGroup={[<EditMapIndicator />]}
                    />
                    <FunctionalButton
                        className="absolute bottom-2 right-2 h-10 w-15 rounded-r5 bg-primary"
                        visible={!isEditLandmarksMode}
                        title="Save"
                    />
                    <MapView map={selectedMap} ref={navigationMapRef} />
                </div>
            </div>
            <Portal
                visible={isEditLandmarksMode && selectedLandmark !== null}
                target={document.getElementById("edit-map-main-content")}
            >
                <EditLandmarkPanel
                    className="absolute right-2 top-2"
                    landmark={selectedLandmark}
                    setLandmark={setSelectedLandmark}
                />
            </Portal>
        </div>
    );
};

export default EditMapPage;
